import { groupBy, sortBy } from 'es-toolkit';
import { get } from 'es-toolkit/compat';

import type { Key } from 'antd/es/table/interface';

import dayjs from '@skynet/formatting-date';

import type { JsonTransaction } from 'src/store/shared/transactions/types';
import type {
  HistoryDataItem,
  InitialBalanceItem,
  JsonBalanceHistoryAsset,
  JsonBalanceHistoryExtended,
  JsonBalanceHistoryItem,
  NewComparisonItem,
  PriceHistoryItemType,
} from 'src/store/shared/balance-history/types';
import { AssetType } from '@skynet/types-asset-type';
import type { JsonBalanceAsset } from 'src/store/shared/balances/types';

import type { NumberFormatterOptions } from '@skynet/sky-number';
import { formatNumber } from '@skynet/sky-number';

import { getPrice, isNotAssetData } from '../core/helpers';
import { smallTokensThreshold } from '../core/constants';
import type { NaiveLocalDateTime } from 'src/store/shared/balance-history/helpers';

const defaultFormat: NumberFormatterOptions = {
  style: 'currency',
  format: 'precision' as const,
};

export const getDiffItem = (
  startItem: HistoryDataItem & Partial<JsonBalanceHistoryItem>,
  endItem: HistoryDataItem & Partial<JsonBalanceHistoryItem>,
  transactionsDiff = { volume: 0, volumeUSD: 0 },
): HistoryDataItem => {
  const isDerivative =
    startItem.assetType === AssetType.Position || endItem.assetType === AssetType.Position;
  const tokenFormat: NumberFormatterOptions = {
    ...defaultFormat,
    ...(!isDerivative ? { currency: endItem.token } : { currency: 'USD' }),
  };
  const usdFormat: NumberFormatterOptions = { ...defaultFormat, currency: 'USD' };

  // For derivatives use unrealizedPnL; for collateral/spot use volume

  const startVol = isDerivative ? (startItem.unrealizedPnL ?? 0) : (startItem.volume ?? 0);

  const endVol = isDerivative ? (endItem.unrealizedPnL ?? 0) : (endItem.volume ?? 0);

  // For derivatives, hints display raw unrealizedPnL (not converted token volume)
  const startHintVal = isDerivative ? (startItem.unrealizedPnL ?? 0) : startVol;
  const endHintVal = isDerivative ? (endItem.unrealizedPnL ?? 0) : endVol;

  const strStartValue = formatNumber(startHintVal, tokenFormat);
  const strEndValue = formatNumber(endHintVal, tokenFormat);

  // For derivatives, ignore hints from getNewHistoryDataItem (they are volume-based, not PnL-based)
  const strVolumeHint = isDerivative
    ? `${strEndValue} - (${strStartValue})`
    : `${endItem.volumeHint ?? strEndValue} - (${strStartValue})`;

  const startUSD = isDerivative ? (startItem.unrealizedPnL ?? 0) : (startItem.volumeUSD ?? 0);
  const endUSD = isDerivative ? (endItem.unrealizedPnL ?? 0) : (endItem.volumeUSD ?? 0);

  const strStartValueUSD = formatNumber(startUSD, usdFormat);
  const strEndValueUSD = formatNumber(endUSD, usdFormat);
  const strVolumeUSDHint = isDerivative
    ? `${strEndValueUSD} - (${strStartValueUSD})`
    : `${endItem.volumeUSDHint ?? strEndValueUSD} - (${strStartValueUSD})`;

  // Transaction diff only applies to collateral, not to unrealized PnL
  const txDiff = isDerivative ? { volume: 0, volumeUSD: 0 } : transactionsDiff;
  let volume = endVol - startVol - txDiff.volume;
  let volumeUSD: number;

  if (isDerivative) {
    // For derivatives `volume` currently holds the PnL diff in USD.
    volumeUSD = volume;
    volume = endItem.price ? volume / endItem.price : 0;
  } else {
    volumeUSD = volume * (endItem.price ?? 0);
  }

  return {
    price: endItem.price ?? 0,
    isSmallBalance: false,
    volume,
    volumeUSD,
    volumeHint: strVolumeHint,
    volumeUSDHint: strVolumeUSDHint,
  };
};

export const getNewHistoryDataItem = ({
  item,
  isInitialBalanceUsed,
  transactionsDiff = { volume: 0, volumeUSD: 0 },
  priceHistory = {},
  initialValues = {},
}: {
  item?: JsonBalanceHistoryItem;
  isInitialBalanceUsed?: boolean;
  transactionsDiff?: { volume: number; volumeUSD: number };
  priceHistory?: Record<string, PriceHistoryItemType[]>;
  initialValues?: Record<string, InitialBalanceItem>;
}) => {
  const historyData: HistoryDataItem = {
    price: 0,
    volume: 0,
    volumeUSD: 0,
    isSmallBalance: false,
  };

  if (!item) {
    return historyData;
  }

  // Calculate price
  let price = item.lastPrice;
  if (!price && Object.keys(priceHistory).length > 0) {
    // The live balance pairs with the live quote by flag, not by date: the quote is dated in UTC
    // while the live balance is keyed by local "now", and the two are built by separately
    // memoised selectors, so even a local date would drift a minute apart (see getHistoryPrices).
    const historyItemPrice = (priceHistory[item.token] ?? []).find(
      (p) => p.date === item.date || (item.isCurrent && p.isCurrent),
    );
    if (historyItemPrice && historyItemPrice.price != null) {
      price = historyItemPrice.price;
    }
  }
  historyData.price = getPrice({ ...item, price });

  // Calculate volumes
  const itemVolume = item.volume ?? 0;
  const initialValue = isInitialBalanceUsed ? (initialValues[item.token]?.value ?? 0) : 0;

  historyData.volume = itemVolume + initialValue + transactionsDiff.volume;
  historyData.volumeUSD =
    (itemVolume + initialValue) * historyData.price + transactionsDiff.volumeUSD;

  // Format hints
  const formatUsd: NumberFormatterOptions = { ...defaultFormat, currency: 'USD' };
  const formatToken: NumberFormatterOptions = {
    ...defaultFormat,
    currency: item.token,
    currencyPosition: 'none',
  };

  const hints: string[] = [];
  const hintsUSD: string[] = [];

  hints.push(formatNumber(itemVolume, formatToken));
  hintsUSD.push(formatNumber(itemVolume * historyData.price, formatUsd));

  if (isInitialBalanceUsed && initialValue !== 0) {
    hints.push(`(${formatNumber(initialValue, formatToken)} initial)`);
    hintsUSD.push(`(${formatNumber(initialValue * historyData.price, formatUsd)} initial)`);
  }

  if (transactionsDiff.volume !== 0) {
    hints.push(`(${formatNumber(transactionsDiff.volume, formatToken)} transactions)`);
  }

  if (transactionsDiff.volumeUSD !== 0) {
    hintsUSD.push(`(${formatNumber(transactionsDiff.volumeUSD, formatUsd)} transactions)`);
  }

  if (hints.length > 1) {
    historyData.volumeHint = hints.join(' + ');
  }

  if (hintsUSD.length > 1) {
    historyData.volumeUSDHint = hintsUSD.join(' + ');
  }

  if (historyData.price !== 0) {
    historyData.isSmallBalance = Math.abs(historyData.volumeUSD) < smallTokensThreshold;
  }

  return historyData;
};

/**
 * The snapshots and the transaction total a start/end comparison is built from.
 *
 * Resolving them — sorting the history and matching both ends of the range — is the expensive part
 * of a comparison, and it does not depend on how transactions are applied. The compare table and
 * the compare list resolve them once and each finish the comparison with
 * {@link buildComparisonData}.
 */
export interface ComparisonSnapshots {
  startItem: JsonBalanceHistoryItem | undefined;
  endItem: JsonBalanceHistoryItem | undefined;
  transactionsDiff: { volume: number; volumeUSD: number };
}

/**
 * Resolves the start and end snapshots of a comparison range and sums the transactions in it.
 *
 * @param HistoryItems - The history of one account's token.
 * @param transactions - That token's transactions inside the range.
 * @param comparisonStartDate - The range start.
 * @param comparisonEndDate - The range end.
 * @param endSnapshotDate - The picked end snapshot time, or `'current'` for the latest one of the day.
 */
export const getComparisonSnapshots = (
  HistoryItems: JsonBalanceHistoryItem[],
  transactions: JsonTransaction[] = [],
  comparisonStartDate?: NaiveLocalDateTime,
  comparisonEndDate?: NaiveLocalDateTime,
  endSnapshotDate?: string,
): ComparisonSnapshots => {
  // ex. 2024-06-11 00:00 for midnight
  const startDate = comparisonStartDate;

  // By instant, not by the raw string: the live item is keyed "YYYY-MM-DD HH:mm"
  // and backend snapshots "YYYY-MM-DDTHH:mm:ssZ", so a string sort put the live
  // item before every snapshot of its day and "current" picked a stale one.
  const historySortedByDate = sortBy(HistoryItems, [(item) => dayjs(item.date).valueOf()]);

  const startItem = historySortedByDate.find(
    ({ date }) =>
      // this condition is needed to limit the end date to the exact day,
      // remove this condition if we want to fallback to the closest next balance
      dayjs(date).isSameOrBefore(dayjs(startDate).endOf('day')) &&
      dayjs(date).isSameOrAfter(dayjs(startDate)),
  );

  // ex. 2024-06-11 00:00 for midnight
  // ex. 2024-06-11 23:59
  const endDate = comparisonEndDate;

  const endItem = historySortedByDate.findLast(({ date }) => {
    // this condition is needed to limit the end date to the exact day,
    // remove this condition if we want to have a fallback to the closest previous balance
    if (endSnapshotDate === 'current') {
      // Local, not dayjs.utc(): endDate is a naive local calendar-day string
      // (see helpers.ts isComparisonDateNow), so reading it in UTC mode
      // misinterprets its digits as UTC and can miss a real UTC-keyed
      // snapshot whose calendar day differs from the viewer's local one
      // (SKY-4575).
      return (
        dayjs(date).isSameOrAfter(dayjs(endDate).startOf('day')) &&
        dayjs(date).isSameOrBefore(dayjs(endDate).endOf('day'))
      );
    } else {
      return (
        dayjs(date).isSameOrAfter(dayjs(endDate).startOf('day')) &&
        dayjs(date).isSameOrBefore(endDate)
      );
    }
  });

  const transactionsDiff = transactions.reduce(
    (res, transaction) =>
      // return res;
      ({
        volume: res.volume + transaction.volume,
        volumeUSD: res.volumeUSD + transaction.usd,
      }),
    { volume: 0, volumeUSD: 0 },
  );

  return { startItem, endItem, transactionsDiff };
};

/**
 * Builds a start/end comparison from its resolved snapshots.
 *
 * @param snapshots - The resolved snapshots (see {@link getComparisonSnapshots}).
 * @param isInitialBalanceUsed - Whether the initial balance is added to the start value.
 * @param comparisonStartDate - The range start.
 * @param comparisonEndDate - The range end.
 * @param isUseTransitionsDiff - `false` adds the transactions to the start value (compare table),
 * `true` keeps the raw start value and subtracts them from the net change instead (compare list).
 * @param priceHistory - Price series for snapshots without their own price.
 * @param initialValues - The account's initial balances by token.
 * @param endSnapshotDate - The picked end snapshot time, or `'current'`.
 */
export const buildComparisonData = (
  { startItem, endItem, transactionsDiff }: ComparisonSnapshots,
  isInitialBalanceUsed: boolean,
  comparisonStartDate?: NaiveLocalDateTime,
  comparisonEndDate?: NaiveLocalDateTime,
  isUseTransitionsDiff = false,
  priceHistory: Record<string, PriceHistoryItemType[]> = {},
  initialValues: Record<string, InitialBalanceItem> = {},
  endSnapshotDate?: string,
) => {
  const startDate = comparisonStartDate;
  const endDate = comparisonEndDate;

  const startData = getNewHistoryDataItem({
    item: startItem,
    isInitialBalanceUsed,
    transactionsDiff: isUseTransitionsDiff ? undefined : transactionsDiff,
    priceHistory,
    initialValues,
  });

  const endData = getNewHistoryDataItem({
    item: endItem,
    // we should never use initial balance for the end date in comparison
    isInitialBalanceUsed: false,
    priceHistory,
  });

  const diff = getDiffItem(
    { ...startItem, ...startData },
    { ...endItem, ...endData },
    isUseTransitionsDiff ? transactionsDiff : undefined,
  );

  return {
    startDate: startDate || '',
    startData,
    endDate: endDate || '',
    endData,
    startHistoryItem: startItem,
    endHistoryItem: endItem
      ? {
          ...endItem,
          // Display-only: the compare tooltip renders this via SkyDateTimeUTC,
          // which parses it as UTC, so it must carry the real UTC instant —
          // local wall-clock digits would be shown shifted by the UTC offset.
          ...(endSnapshotDate === 'current'
            ? { date: dayjs.utc().format('YYYY-MM-DD HH:mm') }
            : {}),
        }
      : undefined,
    diff,
    isSmallBalance: (startData?.isSmallBalance && endData?.isSmallBalance) ?? false,
  } satisfies NewComparisonItem;
};

/**
 * Resolves the snapshots of a comparison range and builds the comparison in one call — for a
 * single view. See {@link getComparisonSnapshots} and {@link buildComparisonData}.
 */
export const getNewComparisonData = (
  HistoryItems: JsonBalanceHistoryItem[],
  isInitialBalanceUsed: boolean,
  transactions: JsonTransaction[] = [],
  comparisonStartDate?: NaiveLocalDateTime,
  comparisonEndDate?: NaiveLocalDateTime,
  isUseTransitionsDiff = false,
  priceHistory: Record<string, PriceHistoryItemType[]> = {},
  initialValues: Record<string, InitialBalanceItem> = {},
  endSnapshotDate?: string,
) =>
  buildComparisonData(
    getComparisonSnapshots(
      HistoryItems,
      transactions,
      comparisonStartDate,
      comparisonEndDate,
      endSnapshotDate,
    ),
    isInitialBalanceUsed,
    comparisonStartDate,
    comparisonEndDate,
    isUseTransitionsDiff,
    priceHistory,
    initialValues,
    endSnapshotDate,
  );

/**
 * Returns true when an asset represents a derivative (futures / position) instrument.
 * For individual account assets detection relies on the comparison history items;
 * for synthetic summary assets the `assetType` field on the asset itself is used as fallback.
 */
export const isDerivativeHistoryAsset = (
  asset: JsonBalanceHistoryAsset | JsonBalanceAsset,
): boolean => {
  if ('comparison' in asset) {
    if (
      asset.comparison?.startHistoryItem?.assetType === AssetType.Position ||
      asset.comparison?.endHistoryItem?.assetType === AssetType.Position
    ) {
      return true;
    }
    // Fallback for synthetic summary assets that carry no history items
    return asset.assetType === AssetType.Position;
  }
  return asset.assetType === AssetType.Position;
};

export const getCompareSummary = (
  uniqTokens: string[],
  data: JsonBalanceHistoryExtended[],
  selection: Key[],
  isSmallBalancesVisible = false,
  priceHistory: Record<string, PriceHistoryItemType[]>,
  showDerivative: boolean = false,
): JsonBalanceHistoryAsset[] => {
  const allAssets = data.flatMap((item) => item.assets);
  const grouped = groupBy(allAssets, (item) => {
    if (showDerivative && isDerivativeHistoryAsset(item)) {
      return `${item.token}-DERIVATIVE`;
    }
    return item.token;
  });
  /**
   * Compute a total across accounts for the given token + derivative flag,
   * respecting selection / doubling / smallBalance filters.
   */
  const computeTotal = (token: string, key: string, isDerivative: boolean): number =>
    data.reduce((sum, item) => {
      if (selection?.length && !selection.includes(item.key)) return sum;

      const foundAssets = item.assets.filter(
        (a) =>
          a.token === token &&
          (showDerivative ? isDerivativeHistoryAsset(a) === isDerivative : true),
      );

      return foundAssets.reduce((innerSum, foundAsset) => {
        if (item?.isTotalDoubling || foundAsset?.isDoubling) return innerSum;
        if (!isSmallBalancesVisible && foundAsset?.isSmallBalance) return innerSum;

        return innerSum + Number(get(foundAsset, key, 0));
      }, sum);
    }, 0);

  const computePrice = (token: string, key: string, isDerivative: boolean): number => {
    const prices = data.reduce<number[]>((acc, item) => {
      if (selection?.length && !selection.includes(item.key)) return acc;

      const foundAssets = item.assets.filter(
        (a) =>
          a.token === token &&
          (showDerivative ? isDerivativeHistoryAsset(a) === isDerivative : true),
      );

      return foundAssets.reduce((innerAcc, foundAsset) => {
        if (item?.isTotalDoubling || foundAsset?.isDoubling) return innerAcc;
        if (!isSmallBalancesVisible && foundAsset?.isSmallBalance) return innerAcc;

        const price = Number(get(foundAsset, key, 0));
        if (!price) return innerAcc;

        return [...innerAcc, price];
      }, acc);
    }, []);

    if (!prices.length) return 0;
    return prices.reduce((a, b) => a + b, 0) / prices.length;
  };

  return Object.entries(grouped).reduce<JsonBalanceHistoryAsset[]>(
    (result, [groupKey, groupedAssets]) => {
      const isDerivativeGroup = groupKey.endsWith('-DERIVATIVE');
      const token = isDerivativeGroup ? groupKey.replace(/-DERIVATIVE$/, '') : groupKey;

      if (!uniqTokens.includes(token)) return result;

      // Determine token type (quote / base / other)
      const isBase = groupedAssets.some((a) => a.type === 'base');
      const isQuote = groupedAssets.some((a) => a.type === 'quote');
      let tokenType = 'other';
      if (isBase) tokenType = 'base';
      else if (isQuote) tokenType = 'quote';

      // Prices — if single asset use its price directly; otherwise prefer priceHistory
      let startPrice: number;
      let endPrice: number;

      if (groupedAssets.length === 1) {
        startPrice = Number(groupedAssets[0]?.comparison?.startData?.price ?? 0);
        endPrice = Number(groupedAssets[0]?.comparison?.endData?.price ?? 0);
      } else {
        startPrice = priceHistory[token]?.[0]?.price ?? 0;
        endPrice = priceHistory[token]?.at(-1)?.price ?? 0;

        // A one-point series cannot express a start *and* an end: `[0]` and `at(-1)` are the same
        // point, so the summary would claim a 0% change. Both prices then come from the per-account
        // average instead — deliberately both, so the pair is always read from a single source and
        // the diff never mixes a feed start with an averaged end.
        const hasSinglePoint = (priceHistory[token]?.length ?? 0) < 2;
        if (selection.length > 0 || !startPrice || hasSinglePoint) {
          startPrice = computePrice(token, 'comparison.startData.price', isDerivativeGroup);
        }
        if (selection.length > 0 || !endPrice || hasSinglePoint) {
          endPrice = computePrice(token, 'comparison.endData.price', isDerivativeGroup);
        }
      }

      // Dates
      const startDate = data[0]?.assets[0]?.comparison?.startDate ?? '';
      const endDate =
        data[0]?.assets[0]?.comparison?.endHistoryItem?.date ??
        data[0]?.assets[0]?.comparison?.endDate ??
        '';

      // History items (for downstream derivative detection)
      const startHistoryItem = groupedAssets.find((a) => a.comparison?.startHistoryItem)?.comparison
        ?.startHistoryItem;
      const endHistoryItem = groupedAssets.find((a) => a.comparison?.endHistoryItem)?.comparison
        ?.endHistoryItem;

      result.push({
        token,
        type: tokenType,
        assetType: isDerivativeGroup ? AssetType.Position : undefined,
        comparison: {
          startDate,
          startData: {
            price: startPrice,
            volume: computeTotal(token, 'comparison.startData.volume', isDerivativeGroup),
            volumeUSD: computeTotal(token, 'comparison.startData.volumeUSD', isDerivativeGroup),
            isSmallBalance: false,
          },
          endDate,
          endData: {
            price: endPrice,
            volume: computeTotal(token, 'comparison.endData.volume', isDerivativeGroup),
            volumeUSD: computeTotal(token, 'comparison.endData.volumeUSD', isDerivativeGroup),
            isSmallBalance: false,
          },
          diff: {
            price: endPrice,
            volume: computeTotal(token, 'comparison.diff.volume', isDerivativeGroup),
            volumeUSD: computeTotal(token, 'comparison.diff.volumeUSD', isDerivativeGroup),
            isSmallBalance: false,
          },
          startHistoryItem,
          endHistoryItem,
          isSmallBalance: false,
        } satisfies NewComparisonItem,
        lastPrice: endPrice,
        leverage: 0,
        history: [],
      } satisfies JsonBalanceHistoryAsset);

      return result;
    },
    [],
  );
};

export const getCompareAssetInfo = (
  item: JsonBalanceHistoryAsset,
  targetKey: 'volume' | 'volumeUSD' = 'volume',
) => {
  let startValue = get(item?.comparison?.startData, targetKey);
  const startPrice = get(item?.comparison?.startData, 'price');
  const startDate =
    get(item?.comparison.startHistoryItem, 'date') || get(item?.comparison, 'startDate');
  const startHint = get(item?.comparison?.startData, `${targetKey}Hint`);

  let endValue = get(item?.comparison?.endData, targetKey);

  const endPrice = get(item?.comparison?.endData, 'price');
  const endDate = get(item?.comparison.endHistoryItem, 'date') || get(item?.comparison, 'endDate');
  const endHint = get(item?.comparison?.endData, `${targetKey}Hint`);

  if (startValue === 0 && isNotAssetData(item?.comparison?.startData)) {
    startValue = undefined;
  }

  if (endValue === 0 && isNotAssetData(item?.comparison?.endData)) {
    endValue = undefined;
  }

  const diffValue = get(item?.comparison?.diff, targetKey);
  const diffHint = get(item?.comparison?.diff, `${targetKey}Hint`);

  const startPositionType = get(item?.comparison?.startHistoryItem, 'positionType', null);
  const endPositionType = get(item?.comparison?.endHistoryItem, 'positionType', null);

  // Use history-item assetType when available; fall back to the asset-level assetType that is
  // set on synthetic summary assets produced by getCompareSummary.
  const startAssetType = get(item?.comparison?.startHistoryItem, 'assetType') ?? item?.assetType;
  const endAssetType = get(item?.comparison?.endHistoryItem, 'assetType') ?? item?.assetType;

  const startTokenVolume = get(item?.comparison?.startData, 'volume') ?? 0;
  const startVolumeUSD = get(item?.comparison?.startData, 'volumeUSD') ?? 0;

  const endTokenVolume = get(item?.comparison?.endData, 'volume') ?? 0;
  const endVolumeUSD = get(item?.comparison?.endData, 'volumeUSD') ?? 0;

  const endUnrealizedPnL = get(item?.comparison?.endHistoryItem, 'unrealizedPnL');
  const startUnrealizedPnL = get(item?.comparison?.startHistoryItem, 'unrealizedPnL');

  const startPriceDetails = get(item?.comparison?.startHistoryItem, 'priceDetails');
  const endPriceDetails = get(item?.comparison?.endHistoryItem, 'priceDetails');

  return {
    startPositionType,
    startTokenVolume,
    startVolumeUSD,
    startValue,
    startPrice,
    startDate,
    startHint,
    startAssetType,
    startUnrealizedPnL,
    startPriceDetails,

    endPositionType,
    endTokenVolume,
    endVolumeUSD,
    endValue,
    endPrice,
    endDate,
    endHint,
    endAssetType,
    endUnrealizedPnL,
    endPriceDetails,

    diffValue,
    diffHint,
    diffPrice: get(item?.comparison?.diff, 'price', endPrice),
  };
};
